import { useCallback, useMemo } from "react";

import type {
    MonitorAndProtectModelAdsRequestAdPart,
    MonitorAndProtectModelAdsRequestAdsTableRequest,
    MonitorAndProtectModelAdsRequestIncludeExcludeKeywordRule,
    MonitorAndProtectModelAdsRequestMatch,
    MonitorAndProtectModelAdsRequestRuleType,
} from "@similarweb/pro-api-client";

import { mnpNavigation } from "../../../app/navigation";
import { useMonitorAndProtectDataAndParams } from "../../../shared/hooks/navigations";

import type { IAdsFilterState, IIncludeExcludeValue } from "./ads-filters.types";

const adPartMapping: Record<string, MonitorAndProtectModelAdsRequestAdPart> = {
    any: "All",
    title: "Title",
    description: "Description",
};

const matchTypeMapping: Record<string, MonitorAndProtectModelAdsRequestMatch> = {
    partial: "Contains",
    exact: "ExactlyMatches",
};

const ruleTypeMapping: Record<string, MonitorAndProtectModelAdsRequestRuleType> = {
    include: "Include",
    exclude: "Exclude",
};

const transformFilterStateToApiRequest = (
    filterState: IAdsFilterState,
): Partial<MonitorAndProtectModelAdsRequestAdsTableRequest> => {
    const apiRequest: Partial<MonitorAndProtectModelAdsRequestAdsTableRequest> = {};

    if (filterState.adType && filterState.adType.length > 0) {
        apiRequest.adTypes = filterState.adType;
    }

    if (
        (filterState.domains?.include && filterState.domains.include.length > 0) ||
        (filterState.domains?.exclude && filterState.domains.exclude.length > 0)
    ) {
        apiRequest.includeExcludeAdvertiserRules = {};
        if (filterState.domains?.include && filterState.domains.include.length > 0) {
            apiRequest.includeExcludeAdvertiserRules.isOneOf = filterState.domains.include;
        }
        if (filterState.domains?.exclude && filterState.domains.exclude.length > 0) {
            apiRequest.includeExcludeAdvertiserRules.isNotOneOf = filterState.domains.exclude;
        }
    }

    if (filterState.keywords) {
        const { include, exclude } = filterState.keywords;
        if ((include && include.length > 0) || (exclude && exclude.length > 0)) {
            apiRequest.includeExcludeKeywordRules = {
                includeWhenContainsAnyOf: include && include.length > 0 ? include : undefined,
                excludeWhenContainsAnyOf: exclude && exclude.length > 0 ? exclude : undefined,
            } as MonitorAndProtectModelAdsRequestIncludeExcludeKeywordRule;
        }
    }

    if (filterState.adContent && filterState.adContent.length > 0) {
        apiRequest.adContentRules = filterState.adContent
            .filter((rule) => rule.items && rule.items.length > 0)
            .flatMap((rule) => ({
                adPart: adPartMapping[rule.fieldName] || "All",
                match: matchTypeMapping[rule.matchType] || "Contains",
                ruleType: ruleTypeMapping[rule.type] || "Include",
                value: rule.items.map((item) => item.text),
            }));
    }

    if (filterState.avgRank) {
        const { from, to } = filterState.avgRank;
        if (from !== undefined || to !== undefined) {
            apiRequest.avgRank = {
                from: from ?? null,
                to: to ?? null,
            };
        }
    }

    return apiRequest;
};

export const useFilterFactoryStateFromParams = () => {
    const { dataFilters } = useMonitorAndProtectDataAndParams();

    const filterState = useMemo(() => (dataFilters || {}) as IAdsFilterState, [dataFilters]);

    const apiRequestFilters = useMemo(() => transformFilterStateToApiRequest(filterState), [filterState]);

    return {
        filterState,
        apiRequestFilters,
    };
};

export const useDomainFilterQuickApply = () => {
    const { dataFilters } = useMonitorAndProtectDataAndParams();
    const { domains } = dataFilters as { domains?: IIncludeExcludeValue };

    const toggleField = useCallback(
        (field: "include" | "exclude", domain: string) => {
            const parse = () => {
                if (!domains) {
                    return {
                        [field]: [domain],
                    };
                }

                const fieldValues = domains[field] || [];
                if (fieldValues.includes(domain)) {
                    const res = fieldValues.filter((d) => d !== domain);
                    return {
                        ...domains,
                        [field]: res.length > 0 ? res : undefined,
                    };
                }

                return {
                    ...domains,
                    [field]: [...fieldValues, domain],
                };
            };

            const result = parse();
            const cleanResponse = Object.values(result).filter(Boolean).length > 0 ? result : undefined;
            return cleanResponse;
        },
        [domains],
    );

    const toggleInclude = useCallback(
        (domain: string) => {
            mnpNavigation.updateQueryParams({
                dataFilters: {
                    ...dataFilters,
                    domains: toggleField("include", domain),
                },
            });
        },
        [dataFilters, toggleField],
    );

    const toggleExclude = useCallback(
        (domain: string) => {
            mnpNavigation.updateQueryParams({
                dataFilters: {
                    ...dataFilters,
                    domains: toggleField("exclude", domain),
                },
            });
        },
        [dataFilters, toggleField],
    );

    return {
        toggleInclude,
        toggleExclude,
    };
};
